import * as z from 'zod'
import Router from 'next/router'
import type { Revalidator, RevalidatorOptions } from 'swr/_internal'

import { FetchError } from './fetcher'
import { getSnippetCode } from './snippetCode'
import { config } from 'lib/config'
import { storage } from 'lib/storage'

const CreateContainerVersionResponseSchema = z.object({
	containerVersion: z.object({
		containerVersionId: z.string(),
	}),
})

export const GTM_API_LINK = 'https://tagmanager.googleapis.com/tagmanager/v2'
export const GTM_BEARER_TOKEN_SESSION_STORAGE_KEY = 'gtm_token'

export const GTM_SCOPES = [
	'https://www.googleapis.com/auth/tagmanager.edit.containers',
	'https://www.googleapis.com/auth/tagmanager.edit.containerversions',
	'https://www.googleapis.com/auth/tagmanager.publish',
] as const

export const accountsSchema = z.object({
	account: z
		.array(
			z.object({
				accountId: z.string(),
				name: z.string(),
			}),
		)
		.optional(),
})

export type AccountsData = z.infer<typeof accountsSchema>

export type Account = NonNullable<AccountsData['account']>[number]

export const containersSchema = z.object({
	container: z
		.array(
			z.object({
				accountId: z.string(),
				containerId: z.string(),
				name: z.string(),
			}),
		)
		.optional(),
})

export type ContainersData = z.infer<typeof containersSchema>

export type Container = NonNullable<ContainersData['container']>[number]

export const workspacesSchema = z.object({
	workspace: z.array(
		z.object({
			accountId: z.string(),
			containerId: z.string(),
			workspaceId: z.string(),
			name: z.string(),
		}),
	),
})

export type WorkspacesData = z.infer<typeof workspacesSchema>

export type Workspace = WorkspacesData['workspace'][number]

export const getGTMToken = () => {
	return storage.getItem(GTM_BEARER_TOKEN_SESSION_STORAGE_KEY)
}

export const setGTMToken = (token: string) => {
	return storage.setItem(GTM_BEARER_TOKEN_SESSION_STORAGE_KEY, token)
}

const removeGTMToken = () => {
	storage.removeItem(GTM_BEARER_TOKEN_SESSION_STORAGE_KEY)
}

export const getGTMTokenFromHash = (url: string): string | null => {
	const [, hash] = url.split('#')
	const hashParams = new URLSearchParams(hash)

	const accessToken = hashParams.get('access_token')
	const obtainedScopes = hashParams.get('scope')?.split(' ')
	const allScopesMatch = GTM_SCOPES.every((scope) =>
		// scopes from url are not encoded properly
		obtainedScopes?.includes(scope.replace('https://', 'https:/')),
	)

	if (accessToken && allScopesMatch) {
		return accessToken
	}

	return null
}

export const getGoogleRedirectUrl = (currentPath: string): URL => {
	const url = new URL('https://accounts.google.com/o/oauth2/v2/auth')

	const [basePath, queryString] = currentPath.split('?')
	const params = new URLSearchParams(queryString)
	const templateId = params.get('tid')

	url.searchParams.append('client_id', String(config.googleClientId))
	url.searchParams.append('redirect_uri', `${window.location.origin}${basePath}`)
	url.searchParams.append('scope', GTM_SCOPES.join(' '))
	url.searchParams.append('response_type', 'token')

	if (templateId) {
		url.searchParams.append('state', templateId)
	}

	return url
}

export const gtmSWRErrorHandler = (
	error: Error,
	revalidate: Revalidator,
	{ retryCount }: Required<RevalidatorOptions>,
) => {
	if ('status' in error && typeof error.status === 'number' && error.status === 429 && retryCount < 10) {
		setTimeout(() => revalidate({ retryCount }), 5000)
	}
}

export const gtmFetcher = async <D>(
	{ url, schema }: { url: string; schema?: z.ZodSchema<D> },
	options?: RequestInit,
): Promise<D> => {
	const token = getGTMToken()
	const response = await fetch(url, {
		headers: {
			...options?.headers,
			...(token && { Authorization: `Bearer ${token}` }),
			...(options?.body && { 'Content-Type': 'application/json' }),
		},
		...options,
	})

	if (response.status === 401) {
		Router.push('gtm/sign-in', undefined, { shallow: true })
		removeGTMToken()
	}

	if (response.status === 429) {
		const error = new Error('Too many requests') as FetchError
		error.status = response.status
		throw error
	}

	let data = {}

	if (response.headers.get('content-type')?.includes('application/json')) {
		data = await response.json()
	}

	if (response.ok) {
		if (schema) {
			const parsed = schema.safeParse(data)

			if (parsed.success) {
				return parsed.data
			} else {
				const error = new Error('GTM schema validation has failed') as FetchError
				error.data = data
				throw error
			}
		}

		return data as D
	}

	const error = new Error('Unable to fetch') as FetchError
	error.status = response.status
	error.data = data

	throw error
}

export const createTag = async (
	accountId: string,
	containerId: string,
	workspaceId: string,
	organizationId: string,
	templateId: string,
): Promise<void> => {
	await gtmFetcher<void>(
		{
			url: `${GTM_API_LINK}/accounts/${accountId}/containers/${containerId}/workspaces/${workspaceId}/tags`,
		},
		{
			method: 'POST',
			body: JSON.stringify({
				name: 'OpenWidget',
				type: 'html',
				firingTriggerId: ['2147479553'],
				parameter: [
					{
						type: 'template',
						key: 'html',
						value: getSnippetCode({
							organizationId,
							integrationName: 'gtm',
							...(templateId && templateId !== organizationId && { templateId }),
						}),
					},
					{ type: 'boolean', key: 'supportDocumentWrite', value: 'false' },
				],
				tagFiringOption: 'oncePerEvent',
			}),
		},
	)
}

const createContainerVersion = async (accountId: string, containerId: string, workspaceId: string): Promise<string> => {
	const data = await gtmFetcher(
		{
			url: `${GTM_API_LINK}/accounts/${accountId}/containers/${containerId}/workspaces/${workspaceId}:create_version`,
			schema: CreateContainerVersionResponseSchema,
		},
		{
			method: 'POST',
			body: JSON.stringify({
				name: 'OpenWidget tracking code published',
				notes:
					"Thanks for using OpenWidget's and GTM integration. Your own widget should be already visible on your website, awesome!",
			}),
		},
	)

	return data.containerVersion.containerVersionId
}

const publishVersion = async (accountId: string, containerId: string, versionId: string): Promise<void> => {
	await gtmFetcher<void>(
		{
			url: `${GTM_API_LINK}/accounts/${accountId}/containers/${containerId}/versions/${versionId}:publish`,
		},
		{
			method: 'POST',
		},
	)
}

export const publishOWTag = async (
	accountId: string,
	containerId: string,
	workspaceId: string,
	organizationId: string,
	templateId: string,
): Promise<void> => {
	await createTag(accountId, containerId, workspaceId, organizationId, templateId)
	const versionId = await createContainerVersion(accountId, containerId, workspaceId)
	await publishVersion(accountId, containerId, versionId)
}
